import { coerceString } from "../safe-data";

/**
 * KONTINUITETSBIBELEN — hjertet af LITRA!s kvalitetssikring.
 *
 * Vedligeholder alle etablerede kendsgerninger om bogen:
 * karakterer, tidslinje, lokationer og fakta — og sikrer
 * at LITRA! aldrig modsiger sig selv på tværs af kapitler.
 */

// ═══════════════════════════════════════════════════════════════════
// TYPER
// ═══════════════════════════════════════════════════════════════════

/** En karakter i bogens verden — alle kendte oplysninger. */
export interface BibleCharacter {
  /** Fuldt navn som brugt i bogen */
  name: string;
  /** Kælenavne, titler, aliaser (fx "hr. Lund", "Far", "Skyggen") */
  aliases: string[];
  /** Alder ved bogens begyndelse */
  ageAtStart?: number;
  /** Alder registreret ved specifikke begivenheder/kapitler */
  ageHistory: AgeHistoryEntry[];
  /** Fysisk udseende */
  appearance: AppearanceProfile;
  /** Personlighed som stikord (fx ["trofast", "temperamentsfuld", "indadvendt"]) */
  personalityTraits: string[];
  /** Baggrund: familie, opvækst, vigtige fortidshændelser */
  background: string;
  /** Ydre mål — hvad vil karakteren opnå? */
  want: string;
  /** Indre behov — hvad har karakteren brug for at lære? */
  need: string;
  /** Konflikt karakteren befinder sig i */
  conflict: { inner: string; outer: string };
  /** Karakterbue fra start til slut */
  arc: { start: string; transformation: string; end: string };
  /** Stemmeprofil — sprogstil, rytme, særpræg */
  voice: VoiceProfile;
  /** Relationer til andre karakterer */
  relationships: CharacterRelationship[];
  /** Dybeste frygt (driver beslutninger) */
  fears: string[];
  /** Hemmeligheder — skjult for andre eller for sig selv */
  secrets: string[];
  /** Emotionelle sår fra fortiden */
  wounds: string[];
  /** Hvad tror karakteren om verden? */
  beliefs: string[];
  /** Nuværende status ved senest skrevne kapitel */
  currentStatus: CharacterStatus;
  /** Kapitel hvor karakteren optrådte første gang */
  firstAppearanceChapter: number;
}

/** Alder ved et givent tidspunkt i historien */
export interface AgeHistoryEntry {
  /** Kapitel eller dato (fx "Kapitel 3", "Sommeren 1995") */
  chapterOrDate: string;
  age: number;
  note?: string;
}

/** Fysisk beskrivelse */
export interface AppearanceProfile {
  height?: string;
  hair?: string;
  eyes?: string;
  /** Særkender (arr, tatoveringer, fregner osv.) */
  distinguishingFeatures?: string[];
  /** Generel beskrivelse hvis detaljer ikke er defineret */
  generalDescription?: string;
}

/** Karakterens unikke sprogstil */
export interface VoiceProfile {
  /** Fx "kort og direkte", "blomstrende og akademisk", "ungdommeligt og slenget" */
  style: string;
  /** Ordforråds-niveau (simpelt, nuanceret, teknisk, osv.) */
  vocabulary: string;
  dialect?: string;
  /** Genkommende vendinger, fyldord, særlige syntaksmønstre */
  speechPatterns?: string[];
}

/** Relation til en anden karakter */
export interface CharacterRelationship {
  characterName: string;
  /** Fx "søster", "rival", "mentor", "kæreste", "fjende" */
  relationshipType: string;
  description: string;
}

/** Karakterens nuværende tilstand */
export interface CharacterStatus {
  alive: boolean;
  currentLocation?: string;
  currentActivity?: string;
  lastSeenChapter: number;
}

// ── Tidslinje ──────────────────────────────────────────────────────

/** En begivenhed på tidslinjen */
export interface TimelineEntry {
  id: string;
  /** Dato, årstal eller narrativ reference (fx "Tre år tidligere") */
  dateOrYear: string;
  /** Hvad skete der */
  event: string;
  /** Karakterer involveret (ved fulde navne som i biblen) */
  charactersInvolved: string[];
  location?: string;
  /** Årstid hvis relevant for stemning/plot */
  season?: string;
  /** Historisk/kulturelt ankerpunkt (fx "under krigen", "efter kollapset") */
  culturalContext?: string;
  /** Kapitel der etablerer denne begivenhed */
  chapterReference?: number;
}

// ── Lokationer ─────────────────────────────────────────────────────

/** Et sted i bogens verden */
export interface BibleLocation {
  name: string;
  aliases?: string[];
  geographicPosition?: string;
  /** Visuel, atmosfærisk beskrivelse */
  description: string;
  /** Sanseoplevelser ved dette sted */
  sensoryDetails: {
    visual?: string;
    sounds?: string;
    smells?: string;
  };
  /** Afstande/rejsetid til andre lokationer */
  distancesTo: DistanceEntry[];
  /** Centrale rum eller underområder */
  keyRooms?: RoomDescription[];
  firstMentionedChapter?: number;
}

/** Afstand mellem to lokationer */
export interface DistanceEntry {
  toLocation: string;
  /** Fx "30 km", "to dages ridetur", "gåafstand" */
  distance: string;
  travelTime?: string;
}

/** Beskrivelse af et rum eller underområde */
export interface RoomDescription {
  name: string;
  description: string;
}

// ── Etablerede fakta ───────────────────────────────────────────────

/** Kategori for et etableret faktum */
export type FactCategory =
  | "world-rule"      // regler for bogens verden (magi, teknologi, fysik)
  | "character-fact"  // kendsgerning om en karakter
  | "plot-event"      // noget der skete i plottet
  | "social-context"  // samfund, kultur, klasse, religion
  | "knowledge"       // hvad en karakter ved og hvornår de lærte det
  | "other";

/** Et etableret faktum der ikke må modsiges */
export interface EstablishedFact {
  id: string;
  /** Den faktuelle påstand, kortfattet og præcis */
  statement: string;
  /** Kapitel der etablerede dette faktum */
  sourceChapter: number;
  category: FactCategory;
  relatedCharacters?: string[];
  relatedLocations?: string[];
}

// ── Samlet bibel ───────────────────────────────────────────────────

/** Den komplette kontinuitetsbibel for ét bogprojekt */
export interface ContinuityBible {
  projectId: string;
  bookTitle: string;
  /** Seneste kapitel biblen er opdateret efter */
  lastUpdatedChapter: number;
  /** Alle karakterer, indekseret ved fuldt navn (lowercase) */
  characters: Record<string, BibleCharacter>;
  timeline: TimelineEntry[];
  locations: BibleLocation[];
  establishedFacts: EstablishedFact[];
  createdAt: string;
  updatedAt: string;
}

// ── Konsistenstjek ─────────────────────────────────────────────────

/** En potentiel modsigelse fundet ved konsistenstjek */
export interface ConsistencyIssue {
  type: "character" | "timeline" | "location" | "fact";
  severity: "warning" | "error";
  description: string;
  /** Tekst-uddrag der udløste advarslen */
  triggerText?: string;
  /** Hvad biblen siger der modsiges */
  bibleReference?: string;
}

/** Resultat af et konsistenstjek */
export interface ConsistencyCheckResult {
  passed: boolean;
  issues: ConsistencyIssue[];
}

// ── AI-opdateringsformat ───────────────────────────────────────────

/**
 * Format LITRA! returnerer når den foreslår opdateringer til biblen.
 * Alle felter er valgfrie — LITRA! sender kun det der er ændret.
 */
export interface BibleUpdatePayload {
  newCharacters?: Partial<BibleCharacter>[];
  updatedCharacters?: Array<{ name: string; updates: Partial<BibleCharacter> }>;
  newTimelineEntries?: Partial<TimelineEntry>[];
  newLocations?: Partial<BibleLocation>[];
  newFacts?: Partial<EstablishedFact>[];
  chapterCompleted?: number;
}

// ═══════════════════════════════════════════════════════════════════
// FACTORY
// ═══════════════════════════════════════════════════════════════════

/**
 * Opretter en tom kontinuitetsbibel til et nyt bogprojekt.
 */
export function createEmptyBible(projectId: string, bookTitle = ""): ContinuityBible {
  const now = new Date().toISOString();
  return {
    projectId,
    bookTitle,
    lastUpdatedChapter: 0,
    characters: {},
    timeline: [],
    locations: [],
    establishedFacts: [],
    createdAt: now,
    updatedAt: now,
  };
}

function normalizeCharacter(raw: unknown, fallbackName: string): BibleCharacter {
  const source = raw && typeof raw === "object" ? raw as Partial<BibleCharacter> : {};
  const appearance = source.appearance && typeof source.appearance === "object" ? source.appearance : {};
  const arc = (source.arc && typeof source.arc === "object" ? source.arc : {}) as Partial<BibleCharacter["arc"]>;
  const voice = (source.voice && typeof source.voice === "object" ? source.voice : {}) as Partial<VoiceProfile>;
  const conflict = (source.conflict && typeof source.conflict === "object" ? source.conflict : {}) as Partial<BibleCharacter["conflict"]>;
  const status = (source.currentStatus && typeof source.currentStatus === "object" ? source.currentStatus : {}) as Partial<CharacterStatus>;
  return {
    name: coerceString(source.name, fallbackName),
    aliases: Array.isArray(source.aliases) ? source.aliases.filter(item => typeof item === "string") : [],
    ageAtStart: typeof source.ageAtStart === "number" ? source.ageAtStart : undefined,
    ageHistory: Array.isArray(source.ageHistory) ? source.ageHistory : [],
    appearance: {
      ...appearance,
      distinguishingFeatures: Array.isArray(appearance.distinguishingFeatures)
        ? appearance.distinguishingFeatures.filter(item => typeof item === "string")
        : [],
    },
    personalityTraits: Array.isArray(source.personalityTraits)
      ? source.personalityTraits.filter(item => typeof item === "string")
      : [],
    background: coerceString(source.background),
    want: coerceString(source.want),
    need: coerceString(source.need),
    conflict: {
      inner: coerceString(conflict.inner),
      outer: coerceString(conflict.outer),
    },
    arc: {
      start: coerceString(arc.start),
      transformation: coerceString(arc.transformation),
      end: coerceString(arc.end),
    },
    voice: {
      style: coerceString(voice.style),
      vocabulary: coerceString(voice.vocabulary),
      dialect: typeof voice.dialect === "string" ? voice.dialect : undefined,
      speechPatterns: Array.isArray(voice.speechPatterns)
        ? voice.speechPatterns.filter(item => typeof item === "string")
        : [],
    },
    relationships: Array.isArray(source.relationships) ? source.relationships : [],
    fears: Array.isArray(source.fears) ? source.fears.filter(item => typeof item === "string") : [],
    secrets: Array.isArray(source.secrets) ? source.secrets.filter(item => typeof item === "string") : [],
    wounds: Array.isArray(source.wounds) ? source.wounds.filter(item => typeof item === "string") : [],
    beliefs: Array.isArray(source.beliefs) ? source.beliefs.filter(item => typeof item === "string") : [],
    currentStatus: {
      alive: status.alive !== false,
      currentLocation: typeof status.currentLocation === "string" ? status.currentLocation : undefined,
      currentActivity: typeof status.currentActivity === "string" ? status.currentActivity : undefined,
      lastSeenChapter: typeof status.lastSeenChapter === "number" ? status.lastSeenChapter : 0,
    },
    firstAppearanceChapter: typeof source.firstAppearanceChapter === "number" ? source.firstAppearanceChapter : 0,
  };
}

/** Sikrer at gemt bibel altid har den form UI forventer — undgår render-crash. */
export function normalizeContinuityBible(raw: unknown, projectId: string): ContinuityBible {
  const base = createEmptyBible(projectId);
  if (!raw || typeof raw !== "object") return base;
  const source = raw as Partial<ContinuityBible>;
  const characters: Record<string, BibleCharacter> = {};
  if (source.characters && typeof source.characters === "object") {
    for (const [key, value] of Object.entries(source.characters)) {
      characters[key] = normalizeCharacter(value, key);
    }
  }
  const timeline = Array.isArray(source.timeline)
    ? source.timeline.map((entry, index) => {
        const row = entry && typeof entry === "object" ? entry as Partial<TimelineEntry> : {};
        return {
          id: coerceString(row.id, `timeline-${index + 1}`),
          dateOrYear: coerceString(row.dateOrYear),
          event: coerceString(row.event),
          charactersInvolved: Array.isArray(row.charactersInvolved)
            ? row.charactersInvolved.filter(item => typeof item === "string")
            : [],
          location: typeof row.location === "string" ? row.location : undefined,
          season: typeof row.season === "string" ? row.season : undefined,
          culturalContext: typeof row.culturalContext === "string" ? row.culturalContext : undefined,
          chapterReference: typeof row.chapterReference === "number" ? row.chapterReference : undefined,
        };
      })
    : [];
  const locations = Array.isArray(source.locations)
    ? source.locations.map((entry, index) => {
        const row = entry && typeof entry === "object" ? entry as Partial<BibleLocation> : {};
        const sensory = row.sensoryDetails && typeof row.sensoryDetails === "object" ? row.sensoryDetails : {};
        return {
          name: coerceString(row.name, `Location ${index + 1}`),
          aliases: Array.isArray(row.aliases) ? row.aliases.filter(item => typeof item === "string") : [],
          geographicPosition: typeof row.geographicPosition === "string" ? row.geographicPosition : undefined,
          description: coerceString(row.description),
          sensoryDetails: {
            visual: typeof sensory.visual === "string" ? sensory.visual : undefined,
            sounds: typeof sensory.sounds === "string" ? sensory.sounds : undefined,
            smells: typeof sensory.smells === "string" ? sensory.smells : undefined,
          },
          distancesTo: Array.isArray(row.distancesTo) ? row.distancesTo : [],
          keyRooms: Array.isArray(row.keyRooms) ? row.keyRooms : [],
          firstMentionedChapter: typeof row.firstMentionedChapter === "number" ? row.firstMentionedChapter : undefined,
        };
      })
    : [];
  const establishedFacts = Array.isArray(source.establishedFacts)
    ? source.establishedFacts.map((entry, index) => {
        const row = entry && typeof entry === "object" ? entry as Partial<EstablishedFact> : {};
        return {
          id: coerceString(row.id, `fact-${index + 1}`),
          statement: coerceString(row.statement),
          sourceChapter: typeof row.sourceChapter === "number" ? row.sourceChapter : 0,
          category: row.category || "other",
          relatedCharacters: Array.isArray(row.relatedCharacters)
            ? row.relatedCharacters.filter(item => typeof item === "string")
            : undefined,
          relatedLocations: Array.isArray(row.relatedLocations)
            ? row.relatedLocations.filter(item => typeof item === "string")
            : undefined,
        };
      })
    : [];
  return {
    ...base,
    projectId: coerceString(source.projectId, projectId),
    bookTitle: coerceString(source.bookTitle, base.bookTitle),
    lastUpdatedChapter: typeof source.lastUpdatedChapter === "number" ? source.lastUpdatedChapter : 0,
    characters,
    timeline,
    locations,
    establishedFacts,
    createdAt: coerceString(source.createdAt, base.createdAt),
    updatedAt: coerceString(source.updatedAt, base.updatedAt),
  };
}

// ═══════════════════════════════════════════════════════════════════
// KARAKTER-HELPERS
// ═══════════════════════════════════════════════════════════════════

/** Normalisér et karakternavn til brug som nøgle i `characters`-record */
function charKey(name: string): string {
  return name.trim().toLowerCase();
}

/**
 * Tilføjer en ny karakter til biblen.
 * Returnerer en ny bibel uden at mutere den originale.
 */
export function addCharacter(
  bible: ContinuityBible,
  character: BibleCharacter,
): ContinuityBible {
  const key = charKey(character.name);
  return {
    ...bible,
    characters: { ...bible.characters, [key]: character },
    updatedAt: new Date().toISOString(),
  };
}

/**
 * Opdaterer en eksisterende karakter (delvist — kun de angivne felter ændres).
 * Gør ingenting og returnerer uændret bibel hvis karakteren ikke findes.
 */
export function updateCharacter(
  bible: ContinuityBible,
  name: string,
  updates: Partial<BibleCharacter>,
): ContinuityBible {
  const key = charKey(name);
  const existing = bible.characters[key];
  if (!existing) {
    return addCharacter(bible, buildCharacterFromPartial({ name, ...updates }));
  }
  return {
    ...bible,
    characters: {
      ...bible.characters,
      [key]: { ...existing, ...updates },
    },
    updatedAt: new Date().toISOString(),
  };
}

/**
 * Slår en karakter op ved navn. Returnerer `undefined` hvis ukendt.
 */
export function getCharacter(
  bible: ContinuityBible,
  name: string,
): BibleCharacter | undefined {
  return bible.characters[charKey(name)];
}

/**
 * Returnerer alle karakterer som et array.
 */
export function listCharacters(bible: ContinuityBible): BibleCharacter[] {
  return Object.values(bible.characters);
}

// ═══════════════════════════════════════════════════════════════════
// TIDSLINJE-HELPERS
// ═══════════════════════════════════════════════════════════════════

/**
 * Tilføjer en ny begivenhed til tidslinjen.
 * Genererer automatisk et unikt id hvis ikke angivet.
 */
export function addTimelineEntry(
  bible: ContinuityBible,
  entry: Omit<TimelineEntry, "id"> & { id?: string },
): ContinuityBible {
  const newEntry: TimelineEntry = {
    id: entry.id ?? `tl-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    ...entry,
  };
  return {
    ...bible,
    timeline: [...bible.timeline, newEntry],
    updatedAt: new Date().toISOString(),
  };
}

/**
 * Opdaterer en eksisterende tidslinje-post ved id.
 */
export function updateTimelineEntry(
  bible: ContinuityBible,
  id: string,
  updates: Partial<TimelineEntry>,
): ContinuityBible {
  return {
    ...bible,
    timeline: bible.timeline.map(e => (e.id === id ? { ...e, ...updates } : e)),
    updatedAt: new Date().toISOString(),
  };
}

// ═══════════════════════════════════════════════════════════════════
// LOKATIONS-HELPERS
// ═══════════════════════════════════════════════════════════════════

/**
 * Tilføjer en ny lokation til biblen.
 */
export function addLocation(
  bible: ContinuityBible,
  location: BibleLocation,
): ContinuityBible {
  return {
    ...bible,
    locations: [...bible.locations, location],
    updatedAt: new Date().toISOString(),
  };
}

/**
 * Opdaterer en eksisterende lokation (match ved navn, case-insensitiv).
 */
export function updateLocation(
  bible: ContinuityBible,
  name: string,
  updates: Partial<BibleLocation>,
): ContinuityBible {
  const key = name.trim().toLowerCase();
  return {
    ...bible,
    locations: bible.locations.map(l =>
      l.name.trim().toLowerCase() === key ? { ...l, ...updates } : l
    ),
    updatedAt: new Date().toISOString(),
  };
}

/**
 * Slår en lokation op ved navn (case-insensitiv).
 */
export function getLocation(
  bible: ContinuityBible,
  name: string,
): BibleLocation | undefined {
  const key = name.trim().toLowerCase();
  return bible.locations.find(l => l.name.trim().toLowerCase() === key);
}

// ═══════════════════════════════════════════════════════════════════
// FAKTA-HELPERS
// ═══════════════════════════════════════════════════════════════════

/**
 * Tilføjer et etableret faktum til biblen.
 */
export function addFact(
  bible: ContinuityBible,
  fact: Omit<EstablishedFact, "id"> & { id?: string },
): ContinuityBible {
  const newFact: EstablishedFact = {
    id: fact.id ?? `fact-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    ...fact,
  };
  return {
    ...bible,
    establishedFacts: [...bible.establishedFacts, newFact],
    updatedAt: new Date().toISOString(),
  };
}

/**
 * Returnerer alle fakta i en given kategori.
 */
export function getFactsByCategory(
  bible: ContinuityBible,
  category: FactCategory,
): EstablishedFact[] {
  return bible.establishedFacts.filter(f => f.category === category);
}

// ═══════════════════════════════════════════════════════════════════
// KAPITEL-STATUS
// ═══════════════════════════════════════════════════════════════════

/**
 * Markerer at et kapitel er afsluttet og opdaterer biblen.
 */
export function markChapterComplete(
  bible: ContinuityBible,
  chapterNumber: number,
): ContinuityBible {
  return {
    ...bible,
    lastUpdatedChapter: Math.max(bible.lastUpdatedChapter, chapterNumber),
    updatedAt: new Date().toISOString(),
  };
}

// ═══════════════════════════════════════════════════════════════════
// KONSISTENSTJEK (lokal — heuristisk)
// ═══════════════════════════════════════════════════════════════════

/**
 * Udfører et heuristisk lokalt konsistenstjek af en ny kapiteltekst
 * mod den etablerede bibel.
 *
 * Tjekker:
 * - Karakterer markeret som afdøde der optræder i ny tekst
 * - Mulige nye lokationsnavne der ikke er registreret i biblen
 * - World-rule fakta hvis emne omtales i teksten
 *
 * Bemærk: Dybdegående semantisk tjek foretages af LITRA! via callLiteraJson.
 */
export function checkConsistency(
  bible: ContinuityBible,
  newChapterText: string,
): ConsistencyCheckResult {
  const issues: ConsistencyIssue[] = [];

  // ── Afdøde karakterer der optræder ───────────────────────────────
  for (const char of Object.values(bible.characters)) {
    if (!char.currentStatus.alive) {
      const patterns = [char.name, ...char.aliases].map(
        n => new RegExp(`\\b${escapeRegex(n)}\\b`, "i")
      );
      for (const pattern of patterns) {
        const match = pattern.exec(newChapterText);
        if (match) {
          issues.push({
            type: "character",
            severity: "error",
            description: `"${char.name}" optræder i kapitlet, men er markeret som afdød i biblen (sidst set kapitel ${char.currentStatus.lastSeenChapter}).`,
            triggerText: extractContext(newChapterText, match.index, 60),
            bibleReference: `${char.name}: currentStatus.alive = false`,
          });
          break;
        }
      }
    }
  }

  // ── Mulige ukendte lokationsnavne ────────────────────────────────
  const knownLocationNames = new Set(
    bible.locations
      .flatMap(l => [l.name, ...(l.aliases ?? [])])
      .map(n => n.toLowerCase())
  );
  const locationProbes = newChapterText.matchAll(
    /\b(?:i|til|fra|ved|på)\s+([A-ZÆØÅ][a-zæøåA-ZÆØÅ]+(?:\s+[A-ZÆØÅ][a-zæøåA-ZÆØÅ]+)?)/g
  );
  for (const probe of locationProbes) {
    const candidate = probe[1];
    if (candidate && !knownLocationNames.has(candidate.toLowerCase()) && !isCommonWord(candidate)) {
      issues.push({
        type: "location",
        severity: "warning",
        description: `Mulig ny lokation "${candidate}" optræder, men er ikke registreret i biblen.`,
        triggerText: extractContext(newChapterText, probe.index ?? 0, 60),
      });
    }
  }

  // ── World-rules der kan være berørte ─────────────────────────────
  const worldRules = getFactsByCategory(bible, "world-rule");
  for (const fact of worldRules) {
    const keywords = fact.statement.split(/\s+/).filter(w => w.length > 5);
    if (keywords.length >= 3) {
      const allPresent = keywords.every(kw =>
        new RegExp(`\\b${escapeRegex(kw)}\\b`, "i").test(newChapterText)
      );
      if (allPresent) {
        issues.push({
          type: "fact",
          severity: "warning",
          description: `Teksten berører en world-rule — tjek at den ikke modsiges: "${fact.statement}"`,
          bibleReference: `Etableret i kapitel ${fact.sourceChapter}`,
        });
      }
    }
  }

  return {
    passed: issues.filter(i => i.severity === "error").length === 0,
    issues,
  };
}

function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function extractContext(text: string, index: number, radius: number): string {
  const start = Math.max(0, index - radius);
  const end = Math.min(text.length, index + radius);
  return (start > 0 ? "…" : "") + text.slice(start, end) + (end < text.length ? "…" : "");
}

const COMMON_WORDS = new Set([
  "januar","februar","marts","april","maj","juni","juli","august","september",
  "oktober","november","december","mandag","tirsdag","onsdag","torsdag","fredag",
  "lørdag","søndag","europa","verden","danmark","sverige","norge","england",
]);
function isCommonWord(word: string): boolean {
  return COMMON_WORDS.has(word.toLowerCase());
}

// ═══════════════════════════════════════════════════════════════════
// LOCALSTORAGE PERSISTENS
// ═══════════════════════════════════════════════════════════════════

import { getActiveStorageUserId, sanitizeUserId } from "../user-storage-scope";

function bibleStorageKey(projectId: string): string {
  const uid = getActiveStorageUserId();
  if (!uid) return `bbg-bible-${projectId}`;
  return `bbg-${sanitizeUserId(uid)}-bible-${projectId}`;
}

/**
 * Gemmer biblen til localStorage under en bruger-scopet nøgle.
 */
export function saveBible(projectId: string, bible: ContinuityBible): void {
  try {
    localStorage.setItem(bibleStorageKey(projectId), JSON.stringify(bible));
  } catch {
    console.warn("[LITRA!] Kunne ikke gemme bibel til localStorage");
  }
}

/**
 * Indlæser biblen fra localStorage. Returnerer `null` hvis ingen bibel er gemt.
 */
export function loadBible(projectId: string): ContinuityBible | null {
  try {
    const raw = localStorage.getItem(bibleStorageKey(projectId));
    if (!raw) return null;
    return normalizeContinuityBible(JSON.parse(raw), projectId);
  } catch {
    console.warn("[LITRA!] Kunne ikke indlæse bibel fra localStorage");
    return null;
  }
}

/**
 * Sletter biblen for et projekt fra localStorage.
 */
export function clearBible(projectId: string): void {
  localStorage.removeItem(bibleStorageKey(projectId));
}

// ═══════════════════════════════════════════════════════════════════
// SERIALISERING TIL PROMPT
// ═══════════════════════════════════════════════════════════════════

/**
 * Konverterer biblen til en kompakt, struktureret tekstrepræsentation
 * klar til injektion i LITRA!s system- eller user-prompt.
 * Tomme sektioner udelades for at spare tokens.
 */
export function bibleToPromptContext(bible: ContinuityBible): string {
  const sections: string[] = [];

  sections.push(`## KONTINUITETSBIBEL — "${bible.bookTitle || bible.projectId}"`);
  sections.push(`Senest opdateret efter kapitel ${bible.lastUpdatedChapter}.`);

  // Karakterer
  const chars = listCharacters(bible);
  if (chars.length > 0) {
    const charLines = chars.map(c => {
      const aliases = c.aliases.length ? ` (${c.aliases.join(", ")})` : "";
      const age = c.ageAtStart !== undefined ? `, ${c.ageAtStart} år ved start` : "";
      const status = c.currentStatus.alive ? "i live" : "AFDØD";
      const loc = c.currentStatus.currentLocation ? ` · sidst i ${c.currentStatus.currentLocation}` : "";
      const traits = c.personalityTraits.slice(0, 4).join(", ");
      const appearanceParts = [
        c.appearance.hair && `hår: ${c.appearance.hair}`,
        c.appearance.eyes && `øjne: ${c.appearance.eyes}`,
        c.appearance.height && `højde: ${c.appearance.height}`,
        ...(c.appearance.distinguishingFeatures ?? []),
      ].filter(Boolean);

      let line = `**${c.name}**${aliases}${age} — ${status}${loc}`;
      if (traits) line += `\n  Personlighed: ${traits}`;
      if (appearanceParts.length) line += `\n  Udseende: ${appearanceParts.join("; ")}`;
      if (c.want) line += `\n  Vil: ${c.want}`;
      if (c.need) line += `\n  Behov: ${c.need}`;
      if (c.arc.start) line += `\n  Arc: ${c.arc.start} → ${c.arc.end}`;
      if (c.voice.style) line += `\n  Stemme: ${c.voice.style}`;
      const rels = c.relationships
        .slice(0, 3)
        .map(r => `${r.characterName} (${r.relationshipType})`)
        .join(", ");
      if (rels) line += `\n  Relationer: ${rels}`;
      return line;
    });
    sections.push("### KARAKTERER\n" + charLines.join("\n\n"));
  }

  // Tidslinje
  if (bible.timeline.length > 0) {
    const tlLines = bible.timeline.map(e => {
      const chars = e.charactersInvolved.join(", ");
      const loc = e.location ? ` · ${e.location}` : "";
      return `- [${e.dateOrYear}${loc}] ${e.event}${chars ? ` (${chars})` : ""}`;
    });
    sections.push("### TIDSLINJE\n" + tlLines.join("\n"));
  }

  // Lokationer
  if (bible.locations.length > 0) {
    const locLines = bible.locations.map(l => {
      const aliases = l.aliases?.length ? ` (${l.aliases.join(", ")})` : "";
      let line = `**${l.name}**${aliases}: ${l.description}`;
      const senses = [
        l.sensoryDetails.visual && `visuelt: ${l.sensoryDetails.visual}`,
        l.sensoryDetails.sounds && `lyde: ${l.sensoryDetails.sounds}`,
        l.sensoryDetails.smells && `dufte: ${l.sensoryDetails.smells}`,
      ].filter(Boolean);
      if (senses.length) line += `\n  ${senses.join(" · ")}`;
      const dists = l.distancesTo
        .slice(0, 3)
        .map(d => `${d.toLocation}: ${d.distance}`)
        .join(", ");
      if (dists) line += `\n  Afstande: ${dists}`;
      return line;
    });
    sections.push("### LOKATIONER\n" + locLines.join("\n\n"));
  }

  // World-rules og knowledge-fakta (kritiske — altid med)
  const criticalFacts = bible.establishedFacts.filter(
    f => f.category === "world-rule" || f.category === "knowledge"
  );
  if (criticalFacts.length > 0) {
    const factLines = criticalFacts.map(
      f => `- [${f.category} · kap.${f.sourceChapter}] ${f.statement}`
    );
    sections.push("### ETABLEREDE FAKTA (world-rules & viden)\n" + factLines.join("\n"));
  }

  // Øvrige fakta (kun hvis ikke for mange — holder prompt kompakt)
  const otherFacts = bible.establishedFacts.filter(
    f => f.category !== "world-rule" && f.category !== "knowledge"
  );
  if (otherFacts.length > 0 && otherFacts.length <= 20) {
    const factLines = otherFacts.map(
      f => `- [${f.category} · kap.${f.sourceChapter}] ${f.statement}`
    );
    sections.push("### ØVRIGE FAKTA\n" + factLines.join("\n"));
  }

  return sections.join("\n\n");
}

// ═══════════════════════════════════════════════════════════════════
// KOMPLET KOMPAKT SERIALIZER (Fase 2 — bag VITE_FF_COMPLETE_BIBLE)
// ═══════════════════════════════════════════════════════════════════

/**
 * Komplet-kompakt version af bibleToPromptContext.
 *
 * Stadig kompakt PROSA (ikke pretty-printed JSON), men uden de felter-tab
 * og vilkårlige caps som den oprindelige har. Den bærer nu det som før kun
 * lå i den rå JSON-dump: background, fears, secrets, wounds, beliefs,
 * conflict, arc.transformation, voice (vocabulary/dialect/speechPatterns),
 * relationsbeskrivelser, ALLE fakta (ingen ≤20-gate), samt timeline- og
 * lokations-detaljer. Tomme felter udelades fortsat for at holde det kompakt.
 *
 * Brugt når dobbelt-bibelen er fjernet, så den ene tilbageværende blok
 * stadig giver LITRA fuld karakterdybde uden tidligere kapitler i historik.
 */
export function bibleToPromptContextComplete(bible: ContinuityBible): string {
  const sections: string[] = [];
  const list = (arr?: string[]) => (arr ?? []).filter(Boolean).join(", ");

  sections.push(`## KONTINUITETSBIBEL — "${bible.bookTitle || bible.projectId}"`);
  sections.push(`Senest opdateret efter kapitel ${bible.lastUpdatedChapter}.`);

  // Karakterer — fuld dybde
  const chars = listCharacters(bible);
  if (chars.length > 0) {
    const charLines = chars.map(c => {
      const aliases = c.aliases.length ? ` (${c.aliases.join(", ")})` : "";
      const age = c.ageAtStart !== undefined ? `, ${c.ageAtStart} år ved start` : "";
      const status = c.currentStatus.alive ? "i live" : "AFDØD";
      const loc = c.currentStatus.currentLocation ? ` · sidst i ${c.currentStatus.currentLocation}` : "";
      const activity = c.currentStatus.currentActivity ? ` · gør: ${c.currentStatus.currentActivity}` : "";
      const appearanceParts = [
        c.appearance.hair && `hår: ${c.appearance.hair}`,
        c.appearance.eyes && `øjne: ${c.appearance.eyes}`,
        c.appearance.height && `højde: ${c.appearance.height}`,
        ...(c.appearance.distinguishingFeatures ?? []),
        c.appearance.generalDescription && c.appearance.generalDescription,
      ].filter(Boolean);
      const arcParts = [c.arc.start, c.arc.transformation, c.arc.end].filter(Boolean);
      const voiceParts = [
        c.voice.style && c.voice.style,
        c.voice.vocabulary && `ordforråd: ${c.voice.vocabulary}`,
        c.voice.dialect && `dialekt: ${c.voice.dialect}`,
        c.voice.speechPatterns?.length && `talemønstre: ${list(c.voice.speechPatterns)}`,
      ].filter(Boolean);
      const conflictParts = [
        c.conflict.inner && `indre: ${c.conflict.inner}`,
        c.conflict.outer && `ydre: ${c.conflict.outer}`,
      ].filter(Boolean);

      let line = `**${c.name}**${aliases}${age} — ${status}${loc}${activity}`;
      if (c.personalityTraits.length) line += `\n  Personlighed: ${list(c.personalityTraits)}`;
      if (appearanceParts.length) line += `\n  Udseende: ${appearanceParts.join("; ")}`;
      if (c.background) line += `\n  Baggrund: ${c.background}`;
      if (c.want) line += `\n  Vil: ${c.want}`;
      if (c.need) line += `\n  Behov: ${c.need}`;
      if (conflictParts.length) line += `\n  Konflikt: ${conflictParts.join(" · ")}`;
      if (arcParts.length) line += `\n  Arc: ${arcParts.join(" → ")}`;
      if (voiceParts.length) line += `\n  Stemme: ${voiceParts.join(" — ")}`;
      if (c.fears.length) line += `\n  Frygt: ${list(c.fears)}`;
      if (c.secrets.length) line += `\n  Hemmeligheder: ${list(c.secrets)}`;
      if (c.wounds.length) line += `\n  Sår: ${list(c.wounds)}`;
      if (c.beliefs.length) line += `\n  Overbevisninger: ${list(c.beliefs)}`;
      const rels = c.relationships
        .map(r => `${r.characterName} (${r.relationshipType})${r.description ? `: ${r.description}` : ""}`)
        .join("; ");
      if (rels) line += `\n  Relationer: ${rels}`;
      return line;
    });
    sections.push("### KARAKTERER\n" + charLines.join("\n\n"));
  }

  // Tidslinje — med season, kulturkontekst og kapitelreference
  if (bible.timeline.length > 0) {
    const tlLines = bible.timeline.map(e => {
      const chars = e.charactersInvolved.join(", ");
      const loc = e.location ? ` · ${e.location}` : "";
      const season = e.season ? ` · ${e.season}` : "";
      const culture = e.culturalContext ? ` · kontekst: ${e.culturalContext}` : "";
      const chapRef = typeof e.chapterReference === "number" ? ` (kap. ${e.chapterReference})` : "";
      return `- [${e.dateOrYear}${loc}${season}] ${e.event}${chars ? ` (${chars})` : ""}${culture}${chapRef}`;
    });
    sections.push("### TIDSLINJE\n" + tlLines.join("\n"));
  }

  // Lokationer — med geografisk position, nøglerum og alle afstande
  if (bible.locations.length > 0) {
    const locLines = bible.locations.map(l => {
      const aliases = l.aliases?.length ? ` (${l.aliases.join(", ")})` : "";
      const pos = l.geographicPosition ? ` [${l.geographicPosition}]` : "";
      let line = `**${l.name}**${aliases}${pos}: ${l.description}`;
      const senses = [
        l.sensoryDetails.visual && `visuelt: ${l.sensoryDetails.visual}`,
        l.sensoryDetails.sounds && `lyde: ${l.sensoryDetails.sounds}`,
        l.sensoryDetails.smells && `dufte: ${l.sensoryDetails.smells}`,
      ].filter(Boolean);
      if (senses.length) line += `\n  ${senses.join(" · ")}`;
      const rooms = (l.keyRooms ?? [])
        .map(r => `${r.name}: ${r.description}`)
        .join("; ");
      if (rooms) line += `\n  Rum: ${rooms}`;
      const dists = l.distancesTo
        .map(d => `${d.toLocation}: ${d.distance}${d.travelTime ? ` (${d.travelTime})` : ""}`)
        .join(", ");
      if (dists) line += `\n  Afstande: ${dists}`;
      return line;
    });
    sections.push("### LOKATIONER\n" + locLines.join("\n\n"));
  }

  // Etablerede fakta — ALLE, med relaterede karakterer/lokationer
  const factLine = (f: EstablishedFact) => {
    const rel = [...(f.relatedCharacters ?? []), ...(f.relatedLocations ?? [])].filter(Boolean);
    return `- [${f.category} · kap.${f.sourceChapter}] ${f.statement}${rel.length ? ` {${rel.join(", ")}}` : ""}`;
  };
  const criticalFacts = bible.establishedFacts.filter(
    f => f.category === "world-rule" || f.category === "knowledge"
  );
  if (criticalFacts.length > 0) {
    sections.push("### ETABLEREDE FAKTA (world-rules & viden)\n" + criticalFacts.map(factLine).join("\n"));
  }
  const otherFacts = bible.establishedFacts.filter(
    f => f.category !== "world-rule" && f.category !== "knowledge"
  );
  if (otherFacts.length > 0) {
    sections.push("### ØVRIGE FAKTA\n" + otherFacts.map(factLine).join("\n"));
  }

  return sections.join("\n\n");
}

// ═══════════════════════════════════════════════════════════════════
// PARSING AF AI-SVAR
// ═══════════════════════════════════════════════════════════════════

/**
 * Parser råt AI-svartekst til en `BibleUpdatePayload`.
 * Forventer JSON direkte eller pakket i ```json ... ``` blok.
 * Returnerer `null` ved parseringsfejl.
 */
export function parseBibleUpdatePayload(text: string): BibleUpdatePayload | null {
  const candidates = Array.from(text.matchAll(/```(?:json)?\s*([\s\S]*?)```/gi))
    .map(match => match[1])
    .concat([text]);

  for (const candidate of candidates) {
    const parsed = parseBibleCandidate(candidate ?? "");
    if (parsed) return parsed;
  }
  return null;
}

function parseBibleCandidate(candidate: string): BibleUpdatePayload | null {
  const trimmed = candidate.trim();
  if (!trimmed) return null;

  try {
    const parsed = JSON.parse(trimmed) as BibleUpdatePayload;
    return looksLikeBibleUpdate(parsed) ? parsed : null;
  } catch {
    const objectMatch = /\{[\s\S]*\}/.exec(trimmed);
    if (!objectMatch) return null;
    try {
      const parsed = JSON.parse(objectMatch[0]) as BibleUpdatePayload;
      return looksLikeBibleUpdate(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }
}

function looksLikeBibleUpdate(value: unknown): value is BibleUpdatePayload {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const record = value as Record<string, unknown>;
  return [
    "newCharacters",
    "updatedCharacters",
    "newTimelineEntries",
    "newLocations",
    "newFacts",
    "chapterCompleted",
  ].some(key => key in record);
}

const BIBLE_JSON_KEY_HINT =
  /"(newCharacters|updatedCharacters|newTimelineEntries|newLocations|newFacts|chapterCompleted|currentStatus|personalityTraits|distinguishingFeatures)"/;

function findBareBibleJsonRanges(text: string): Array<[number, number]> {
  const ranges: Array<[number, number]> = [];
  for (let i = 0; i < text.length; i++) {
    if (text[i] !== "{") continue;
    let depth = 0;
    for (let j = i; j < text.length; j++) {
      if (text[j] === "{") depth++;
      else if (text[j] === "}") {
        depth--;
        if (depth === 0) {
          const slice = text.slice(i, j + 1);
          if (parseBibleCandidate(slice)) ranges.push([i, j + 1]);
          break;
        }
      }
    }
  }
  return ranges;
}

/** Remove complete bible-update JSON blocks from user-facing LITRA text. */
export function stripBibleJsonFromText(text: string): string {
  let result = text.replace(/```(?:json)?\s*([\s\S]*?)```/gi, (match, inner: string) => {
    return parseBibleCandidate(String(inner ?? "").trim()) ? "" : match;
  });

  const ranges = findBareBibleJsonRanges(result);
  for (let i = ranges.length - 1; i >= 0; i--) {
    const [start, end] = ranges[i];
    result = `${result.slice(0, start)}${result.slice(end)}`;
  }

  return result.replace(/\n{3,}/g, "\n\n").trim();
}

/** True when visible text is mostly bible-update JSON (not manuscript prose). */
export function isLikelyBibleJsonText(text: string): boolean {
  const trimmed = text.trim();
  if (!trimmed) return false;
  if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
    if (BIBLE_JSON_KEY_HINT.test(trimmed)) return true;
    return Boolean(parseBibleUpdatePayload(trimmed));
  }
  return false;
}

/** Hide in-progress bible JSON while LITRA is still streaming tokens. */
export function stripPartialBibleJsonTail(text: string): string {
  const trimmed = text.trim();
  if (/^\s*"(?:need|voice|background|appearance|want|name|aliases|ageAtStart|currentStatus|personalityTraits|newTimelineEntries)"\s*:/.test(trimmed)) {
    return "";
  }

  const fenceIdx = text.lastIndexOf("```json");
  if (fenceIdx >= 0) return text.slice(0, fenceIdx).trimEnd();

  const bareIdx = text.lastIndexOf("\n{");
  if (bareIdx >= 0) {
    const tail = text.slice(bareIdx + 1);
    if (BIBLE_JSON_KEY_HINT.test(tail)) return text.slice(0, bareIdx).trimEnd();
  }

  const inlineIdx = text.indexOf("{");
  if (inlineIdx === 0 && BIBLE_JSON_KEY_HINT.test(text)) return "";

  if (inlineIdx > 0) {
    const tail = text.slice(inlineIdx);
    if (BIBLE_JSON_KEY_HINT.test(tail)) return text.slice(0, inlineIdx).trimEnd();
  }

  return text;
}

/**
 * Parser en `BibleUpdatePayload` fra LITRA!s svar og anvender
 * alle opdateringer på den eksisterende bibel i ét atomisk skridt.
 * Returnerer uændret bibel ved parseringsfejl.
 */
export function applyBibleUpdate(
  bible: ContinuityBible,
  aiResponseText: string,
): ContinuityBible {
  const payload = parseBibleUpdatePayload(aiResponseText);
  if (!payload) return bible;

  let updated = { ...bible };

  for (const raw of payload.newCharacters ?? []) {
    if (!raw.name) continue;
    updated = addCharacter(updated, buildCharacterFromPartial(raw));
  }

  for (const { name, updates } of payload.updatedCharacters ?? []) {
    if (!name) continue;
    updated = updateCharacter(updated, name, updates);
  }

  for (const raw of payload.newTimelineEntries ?? []) {
    if (!raw.event) continue;
    updated = addTimelineEntry(updated, {
      dateOrYear: raw.dateOrYear ?? "ukendt",
      event: raw.event,
      charactersInvolved: raw.charactersInvolved ?? [],
      location: raw.location,
      season: raw.season,
      culturalContext: raw.culturalContext,
      chapterReference: raw.chapterReference,
    });
  }

  for (const raw of payload.newLocations ?? []) {
    if (!raw.name) continue;
    updated = addLocation(updated, buildLocationFromPartial(raw));
  }

  for (const raw of payload.newFacts ?? []) {
    if (!raw.statement) continue;
    updated = addFact(updated, {
      statement: raw.statement,
      sourceChapter: raw.sourceChapter ?? bible.lastUpdatedChapter,
      category: raw.category ?? "other",
      relatedCharacters: raw.relatedCharacters,
      relatedLocations: raw.relatedLocations,
    });
  }

  if (payload.chapterCompleted !== undefined) {
    updated = markChapterComplete(updated, payload.chapterCompleted);
  }

  return updated;
}

// Hjælpere til applyBibleUpdate
function buildCharacterFromPartial(raw: Partial<BibleCharacter>): BibleCharacter {
  return {
    name: raw.name ?? "Ukendt",
    aliases: raw.aliases ?? [],
    ageAtStart: raw.ageAtStart,
    ageHistory: raw.ageHistory ?? [],
    appearance: raw.appearance ?? {},
    personalityTraits: raw.personalityTraits ?? [],
    background: raw.background ?? "",
    want: raw.want ?? "",
    need: raw.need ?? "",
    conflict: raw.conflict ?? { inner: "", outer: "" },
    arc: raw.arc ?? { start: "", transformation: "", end: "" },
    voice: raw.voice ?? { style: "", vocabulary: "" },
    relationships: raw.relationships ?? [],
    fears: raw.fears ?? [],
    secrets: raw.secrets ?? [],
    wounds: raw.wounds ?? [],
    beliefs: raw.beliefs ?? [],
    currentStatus: raw.currentStatus ?? { alive: true, lastSeenChapter: 0 },
    firstAppearanceChapter: raw.firstAppearanceChapter ?? 0,
  };
}

function buildLocationFromPartial(raw: Partial<BibleLocation>): BibleLocation {
  return {
    name: raw.name ?? "Ukendt sted",
    aliases: raw.aliases,
    geographicPosition: raw.geographicPosition,
    description: raw.description ?? "",
    sensoryDetails: raw.sensoryDetails ?? {},
    distancesTo: raw.distancesTo ?? [],
    keyRooms: raw.keyRooms,
    firstMentionedChapter: raw.firstMentionedChapter,
  };
}

// ═══════════════════════════════════════════════════════════════════
// DEFAULT EXPORT — samlet API
// ═══════════════════════════════════════════════════════════════════

const BibleAPI = {
  createEmpty:        createEmptyBible,
  addCharacter,
  updateCharacter,
  getCharacter,
  listCharacters,
  addTimelineEntry,
  updateTimelineEntry,
  addLocation,
  updateLocation,
  getLocation,
  addFact,
  getFactsByCategory,
  markChapterComplete,
  checkConsistency,
  save:               saveBible,
  load:               loadBible,
  clear:              clearBible,
  toPromptContext:    bibleToPromptContext,
  applyUpdate:        applyBibleUpdate,
  parseUpdatePayload: parseBibleUpdatePayload,
};

export default BibleAPI;
